import createDOMPurify from 'dompurify';

const RICH_TEXT_ALLOWED_TAGS = [
    'a',
    'b',
    'blockquote',
    'br',
    'code',
    'em',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'hr',
    'i',
    'img',
    'li',
    'ol',
    'p',
    'pre',
    'span',
    'strong',
    'sub',
    'sup',
    'table',
    'tbody',
    'td',
    'th',
    'thead',
    'tr',
    'u',
    'ul',
];

const RICH_TEXT_ALLOWED_ATTRS = [
    'class',
    'data',
    'alt',
    'height',
    'href',
    'rel',
    'src',
    'target',
    'width',
];

const DROP_ENTIRE_ELEMENT_TAGS = [
    'iframe',
    'math',
    'object',
    'script',
    'style',
    'svg',
    'template',
];

const SAFE_URL_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

let domPurifyInstance: ReturnType<typeof createDOMPurify> | null | undefined;

export function escapeHtml (value: string): string {
    if (typeof document === 'undefined') {
        return value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    const wrapper = document.createElement('div');
    wrapper.textContent = value;
    return wrapper.innerHTML
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

export function sanitizeRichTextHtml (value?: string | null): string {
    if (!value) return '';

    const domPurify = getDomPurify();
    if (!domPurify) {
        return escapeHtml(value);
    }

    return domPurify.sanitize(value, {
        ALLOWED_ATTR: RICH_TEXT_ALLOWED_ATTRS,
        ALLOWED_TAGS: RICH_TEXT_ALLOWED_TAGS,
        ALLOW_DATA_ATTR: false,
        FORBID_ATTR: ['style'],
        FORBID_TAGS: DROP_ENTIRE_ELEMENT_TAGS,
        KEEP_CONTENT: true,
    });
}

export function stripHtml (value?: string | null): string {
    if (!value) return '';

    if (typeof document === 'undefined') {
        return value.replace(/<[^>]*>?/gm, '').replace(/\s+/g, ' ').trim();
    }

    const safeTemplate = document.createElement('template');
    const htmlWithSpacing = sanitizeRichTextHtml(value)
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<\/(blockquote|h1|h2|h3|h4|h5|h6|li|ol|p|pre|ul)>/gi, ' ');

    safeTemplate.innerHTML = htmlWithSpacing;

    return safeTemplate.content.textContent
        ?.replace(/\u00A0/g, ' ')
        .replace(/\s+/g, ' ')
        .trim() ?? '';
}

export function isRichTextHtmlEmpty (value?: string | null): boolean {
    if (!value) return true;

    if (typeof document === 'undefined') {
        const hasMedia = /<(img|video|iframe)\b/i.test(value);
        const hasText = value.replace(/<[^>]*>?/gm, '').replace(/&nbsp;/g, ' ').trim().length > 0;
        return !hasText && !hasMedia;
    }

    const template = document.createElement('template');
    template.innerHTML = sanitizeRichTextHtml(value);

    const textContent = template.content.textContent?.replace(/\u00A0/g, ' ').trim() ?? '';
    const hasText = textContent.length > 0;
    const hasMedia = template.content.querySelector('img, video, iframe') !== null;

    return !hasText && !hasMedia;
}

function getDomPurify (): ReturnType<typeof createDOMPurify> | null {
    if (domPurifyInstance !== undefined) {
        return domPurifyInstance;
    }

    if (typeof window === 'undefined') {
        return null;
    }

    const domPurify = createDOMPurify(window);
    domPurify.addHook('afterSanitizeAttributes', (node) => {
        if (!(node instanceof window.Element)) {
            return;
        }

        if (node.tagName.toLowerCase() !== 'a') {
            if (node.tagName.toLowerCase() === 'img' && !isSafeImageSrc(node.getAttribute('src'))) {
                node.removeAttribute('src');
            }
            return;
        }

        const href = node.getAttribute('href');
        if (!isSafeHref(href)) {
            node.removeAttribute('href');
            node.removeAttribute('rel');
            node.removeAttribute('target');
            return;
        }

        node.setAttribute('rel', 'noopener noreferrer');
        if (node.getAttribute('target') !== '_blank') {
            node.removeAttribute('target');
        }
    });

    domPurifyInstance = domPurify;
    return domPurifyInstance;
}

function isSafeImageSrc (src: string | null): boolean {
    if (!src) return false;

    const trimmedSrc = src.trim();
    if (!trimmedSrc) return false;
    if (/^data:image\/(png|jpe?g|gif|webp);base64,/i.test(trimmedSrc)) return true;

    try {
        const resolvedUrl = new URL(trimmedSrc, window.location.origin);
        return resolvedUrl.protocol === 'http:' || resolvedUrl.protocol === 'https:';
    } catch {
        return false;
    }
}

function isSafeHref (href: string | null): boolean {
    if (!href) return false;

    const trimmedHref = href.trim();
    if (!trimmedHref) return false;

    if (
        trimmedHref.startsWith('/')
        || trimmedHref.startsWith('./')
        || trimmedHref.startsWith('../')
        || trimmedHref.startsWith('#')
    ) {
        return !trimmedHref.startsWith('//');
    }

    try {
        const resolvedUrl = new URL(trimmedHref, window.location.origin);
        return SAFE_URL_PROTOCOLS.has(resolvedUrl.protocol);
    } catch {
        return false;
    }
}
